/**
 * createMasterTeamFieldHook — fabrique le pattern répété par
 * useMasterAccessScope / useMasterPortalRole / useR4RAccess : une colonne de
 * rectorium_team_members résolue une seule fois par session (cache module),
 * invalidée manuellement après une écriture serveur (RPC) ou au logout.
 * Chaque appel crée son PROPRE cache (fermeture dédiée) — les trois hooks
 * restent des caches indépendants, comme avant cette factorisation.
 */
import { useEffect, useState } from 'react';
import { supabase } from '../services/supabase';

export function createMasterTeamFieldHook({ column, resultKey, mapValue, defaultValue }) {
  let cached; // undefined = jamais chargé ; sinon { email, [resultKey]: value }

  async function fetchField() {
    const { data: userData } = await supabase.auth.getUser();
    const email = userData?.user?.email || null;
    if (!email) return { email: null, [resultKey]: defaultValue };
    const { data, error } = await supabase
      .from('rectorium_team_members')
      .select(column)
      .eq('email', email)
      .maybeSingle();
    // Colonne/ligne absente ou erreur : valeur par défaut — jamais de faux verrou.
    if (error || !data) return { email, [resultKey]: defaultValue };
    return { email, [resultKey]: mapValue(data[column]) };
  }

  function useMasterTeamField(enabled = true) {
    const [state, setState] = useState(() => (cached
      ? { [resultKey]: cached[resultKey], loaded: true }
      : { [resultKey]: defaultValue, loaded: false }));

    useEffect(() => {
      if (!enabled || cached) return;
      let alive = true;
      fetchField()
        .then((result) => {
          cached = result;
          if (alive) setState({ [resultKey]: result[resultKey], loaded: true });
        })
        .catch(() => { if (alive) setState({ [resultKey]: defaultValue, loaded: true }); });
      return () => { alive = false; };
    }, [enabled]);

    return state;
  }

  function invalidateCache() {
    cached = undefined;
  }

  return [useMasterTeamField, invalidateCache];
}
